import React from 'react';
import { PokemonCard, UserCard } from '../../types/pokemon';
import { CardItem } from './CardItem';
import { Heart, Repeat } from 'lucide-react';

interface CardGridProps {
  cards: PokemonCard[];
  loading: boolean;
  collection: Record<number, UserCard>;
  onQuantityChange: (cardId: number, quantity: number) => Promise<void>;
  onCardClick?: (card: PokemonCard) => void;
  selectedCards?: Set<number>;
  isBulkMode?: boolean;
  onWantedToggle: (cardId: number, currentValue: boolean) => void;
  onTradableToggle: (cardId: number, currentValue: boolean) => void;
  showTradeButtons?: (card: PokemonCard) => boolean;
  className?: string;
  getPathForLang: (path: string, targetLang?: 'fr' | 'en') => string;
  setInfo?: {
    display_name: string;
    display_name_en?: string;
  };
}

export function CardGrid({
  cards,
  loading,
  collection,
  onQuantityChange,
  onCardClick,
  selectedCards,
  isBulkMode,
  onWantedToggle,
  onTradableToggle,
  showTradeButtons = () => true,
  className = '',
  getPathForLang,
  setInfo
}: CardGridProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="bg-gray-100 rounded-xl aspect-[3/4] animate-pulse"
          />
        ))}
      </div>
    );
  }

  const handleButtonClick = (e: React.MouseEvent, callback: () => void) => {
    e.preventDefault();
    e.stopPropagation();
    callback();
  };

  return (
    <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 ${className}`}>
      {cards.map((card) => {
        const userCard = collection?.[card.id] || { quantity: 0, wanted: false, tradable: false };
        const isCardTradeable = showTradeButtons(card);
        
        // console.log('Card:', card.name, 'Tradeable:', isCardTradeable); // Debug

        return (
          <div
            key={card.id}
            className="relative group bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300"
          >
            <CardItem
              card={card}
              quantity={userCard.quantity}
              onUpdateQuantity={onQuantityChange}
              onClick={() => onCardClick?.(card)}
              isSelected={selectedCards?.has(card.id)}
              isBulkMode={isBulkMode}
              getPathForLang={getPathForLang}
              setInfo={setInfo}
            />

            {isCardTradeable && (
              <div 
                className="absolute bottom-[25%] left-0 right-0 flex justify-center gap-2 
                  transition-transform duration-200 transform hover:scale-105"
                style={{ zIndex: 20 }}
              >
                <button
                  onClick={(e) => handleButtonClick(e, () => onWantedToggle(card.id, userCard.wanted))}
                  className={`p-2 rounded-full transition-colors shadow-lg backdrop-blur-sm ${
                    userCard.wanted
                      ? 'bg-red-100/90 text-red-600 hover:bg-red-200'
                      : 'bg-white/90 text-gray-600 hover:bg-gray-100'
                  }`}
                  title={userCard.wanted ? "Retirer des souhaits" : "Ajouter aux souhaits"}
                >
                  <Heart className="w-4 h-4" fill={userCard.wanted ? "currentColor" : "none"} />
                </button>
                
                <button
                  onClick={(e) => handleButtonClick(e, () => onTradableToggle(card.id, userCard.tradable))}
                  className={`p-2 rounded-full transition-colors shadow-lg backdrop-blur-sm ${
                    userCard.tradable
                      ? 'bg-green-100/90 text-green-600 hover:bg-green-200'
                      : 'bg-white/90 text-gray-600 hover:bg-gray-100'
                  }`}
                  title={userCard.tradable ? "Retirer des échangeables" : "Marquer comme échangeable"}
                >
                  <Repeat className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}